import { Button, type ButtonProps } from '@chakra-ui/react/button';
import { Skeleton } from '@chakra-ui/react/skeleton';
import { chakra, type HTMLChakraProps, type RecipeProps, useRecipe } from '@chakra-ui/react/styled-system';
import {
  Children,
  cloneElement,
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';

import { type BitkitIconComponent } from '../../icons';

type BitkitButtonOmittedFromButton =
  | 'asChild'
  | 'children'
  | 'colorPalette'
  | 'disabled'
  | 'loading'
  | 'loadingText'
  | 'spinner'
  | 'spinnerPlacement';

interface BitkitButtonCommonProps {
  icon?: BitkitIconComponent;
  suffixIcon?: BitkitIconComponent;
}

export interface BitkitButtonAsButtonProps
  extends BitkitButtonCommonProps, Omit<ButtonProps, BitkitButtonOmittedFromButton> {
  asChild?: false;
  children: string;
  href?: undefined;
  isExternal?: undefined;
  state?: 'disabled' | 'loading' | 'skeleton';
}

export interface BitkitButtonAsAnchorProps
  extends BitkitButtonCommonProps, Omit<HTMLChakraProps<'a'>, 'children' | 'colorPalette'>, RecipeProps<'button'> {
  asChild?: false;
  children: string;
  href: string;
  isExternal?: boolean;
  state?: 'disabled' | 'skeleton';
}

interface BitkitButtonAsChildProps extends BitkitButtonCommonProps, Omit<ButtonProps, BitkitButtonOmittedFromButton> {
  asChild: true;
  children: ReactElement;
  href?: undefined;
  isExternal?: undefined;
  state?: 'disabled' | 'loading' | 'skeleton';
}

export type BitkitButtonProps = BitkitButtonAsButtonProps | BitkitButtonAsAnchorProps | BitkitButtonAsChildProps;

// The forwarded ref is typed as HTMLElement to cover all three modes (button, anchor, slot/asChild),
// since asChild can render any element (router Link, custom wrappers, etc.). Inner narrow casts
// satisfy the wrapped Chakra components without affecting the consumer-visible ref shape.
const BitkitButton = forwardRef<HTMLElement, BitkitButtonProps>((props, ref) => {
  const buttonRecipe = useRecipe({ key: 'button' });
  const Icon = props.icon;
  const SuffixIcon = props.suffixIcon;
  // Resolve the size the recipe would apply before branching on it: an unset `size` reaches us as
  // `undefined` because the recipe's default lives in CSS, not on the prop. Keep the fallback equal
  // to `defaultVariants.size` in Button.recipe.ts.
  const iconSize = (props.size ?? 'lg') === 'lg' ? '24' : '16';
  const isDisabled = props.state === 'disabled';
  const isSkeleton = props.state === 'skeleton';

  // --- Slot mode (asChild) ---
  if (props.asChild) {
    const { asChild: _asChild, children, icon: _icon, state, suffixIcon: _suffixIcon, ...buttonRest } = props;
    const onlyChild = Children.only(children) as ReactElement<{ children?: ReactNode }>;
    const buttonElement = (
      <Button
        asChild
        disabled={isDisabled}
        loading={state === 'loading'}
        ref={ref as Ref<HTMLButtonElement>}
        {...buttonRest}
      >
        {cloneElement(
          onlyChild,
          undefined,
          <>
            {Icon && <Icon marginInlineStart="-4" size={iconSize} />}
            {onlyChild.props.children}
            {SuffixIcon && <SuffixIcon marginInlineEnd="-4" size={iconSize} />}
          </>,
        )}
      </Button>
    );
    return isSkeleton ? (
      <Skeleton asChild loading>
        {buttonElement}
      </Skeleton>
    ) : (
      buttonElement
    );
  }

  // --- Anchor mode (href) ---
  if (props.href !== undefined) {
    const {
      children,
      css,
      href,
      icon: _icon,
      isExternal,
      onClick,
      onKeyDown,
      rel,
      size,
      state: _state,
      suffixIcon: _suffixIcon,
      target,
      variant,
      ...anchorRest
    } = props;
    const effectiveTarget = isExternal ? '_blank' : target;
    const effectiveRel = isExternal ? (rel ? `${rel} noreferrer noopener` : 'noreferrer noopener') : rel;
    const handleClick = isDisabled
      ? (e: MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          e.stopPropagation();
        }
      : onClick;
    const handleKeyDown = isDisabled
      ? (e: KeyboardEvent<HTMLAnchorElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      : onKeyDown;
    const anchorElement = (
      <chakra.a
        ref={ref as Ref<HTMLAnchorElement>}
        {...anchorRest}
        aria-disabled={isDisabled || undefined}
        // A native <a> can't be `:disabled`, so mark `data-disabled` too: it's the styling/interaction hook the
        // recipe's `_hover`/`_active` conditions exclude (`:not(…, [data-disabled])`), while `aria-disabled` stays
        // for a11y. Without it, hover/active would still match a disabled anchor and override the disabled colors.
        css={[buttonRecipe({ size, variant }), css]}
        data-disabled={isDisabled || undefined}
        href={isDisabled ? undefined : href}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        rel={effectiveRel}
        target={effectiveTarget}
      >
        {Icon && <Icon marginInlineStart="-4" size={iconSize} />}
        {children}
        {SuffixIcon && <SuffixIcon marginInlineEnd="-4" size={iconSize} />}
      </chakra.a>
    );
    return isSkeleton ? (
      <Skeleton asChild loading>
        {anchorElement}
      </Skeleton>
    ) : (
      anchorElement
    );
  }

  // --- Button mode (default) ---
  const { children, icon: _icon, state, suffixIcon: _suffixIcon, ...buttonRest } = props;
  return (
    <Skeleton asChild loading={isSkeleton}>
      <Button disabled={isDisabled} loading={state === 'loading'} ref={ref as Ref<HTMLButtonElement>} {...buttonRest}>
        {Icon && <Icon marginInlineStart="-4" size={iconSize} />}
        {children}
        {SuffixIcon && <SuffixIcon marginInlineEnd="-4" size={iconSize} />}
      </Button>
    </Skeleton>
  );
});

BitkitButton.displayName = 'BitkitButton';

export default BitkitButton;
